/**
 * AnnouncementHost — app-wide mount (inside /app) that shows the one-time announcement
 * modal on app open. Thin wrapper over useAnnouncements, mirroring the other global
 * hosts. Also owns the account-switcher sheet so a button's `inAppAction:accountSwitcher`
 * can open it after the announcement closes. See specs/in-app-announcements/spec.md.
 */

import React, { useCallback, useState } from 'react';
import { useAnnouncements } from '../../../src/announcements/hooks/useAnnouncements';
import { OrgSwitcherSheet } from '../../../src/accounts/components/OrgSwitcher/OrgSwitcherSheet';
import type { AnnouncementInAppAction } from '../../../src/announcements/types/announcements.types';
import { AnnouncementModal } from '../AnnouncementModal/AnnouncementModal';
import { runAfterNativeModalDismiss } from '../../../src/shared/helpers/nativeModalDismiss.helper';

export function AnnouncementHost(): React.ReactElement {
  const { visible, blocks, onDismiss } = useAnnouncements();
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const handleInAppAction = useCallback((action: AnnouncementInAppAction) => {
    // The announcement closes in the same tap; presenting the sheet before it is gone is
    // refused on iOS and leaves the app unresponsive (specs/profile-page, Story 8).
    if (action === 'accountSwitcher') runAfterNativeModalDismiss(() => setSwitcherOpen(true));
  }, []);

  return (
    <>
      <AnnouncementModal
        visible={visible}
        blocks={blocks}
        onDismiss={onDismiss}
        onInAppAction={handleInAppAction}
      />
      <OrgSwitcherSheet visible={switcherOpen} onClose={() => setSwitcherOpen(false)} />
    </>
  );
}
